<script setup lang="ts">
import "@cisco-u/button/cu-button.js";
import "@cisco-u/icon-garden/cu-icon-garden.js";
import "@cisco-u/icons/icons/cu-icon-color-stars.js";
import "@cisco-u/icons/icons/cu-icon-curved-rectangle.js";
import "@cisco-u/icons/icons/cu-icon-lab.js";
import "@cisco-u/icons/icons/cu-icon-spiked-circle.js";
import "@cisco-u/new-card/cu-new-card.js";
import "@cisco-u/tags/cu-tags.js";

import { computed, onMounted, ref } from "vue";

import AnchorTargetBlank from "@/components/AnchorTargetBlank.vue";
import RawHtmlBlock from "@/components/RawHtmlBlock.vue";
import tagList from "@/components/tagList.vue";
import { useCardActions } from "@/composables/useCardActions";
import { type CardProps, useCardData } from "@/composables/useCardData";
import { useCardIcons } from "@/composables/useCardIcons";
import { usePricing } from "@/composables/usePricing";
import { useRevUpCampaign } from "@/composables/useRevUpCampaign";
import { isCmlCloudProductId } from "@/consts/cmlCloud";
import { CUSTOMER_ADMIN_PORTAL_LABEL } from "@/consts/labels";
import { Statsig } from "@/env/statsig/client";

interface Props extends CardProps {
  cy: string;
  link?: string | number;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isUnauth: false,
  cardView: "Grid",
  priceView: "USD",
  link: "",
  isRemovable: false,
  isAdminContent: false,
  showDescription: true,
});

const emit = defineEmits<{
  navigate: [result: any];
  bookmark: [result: any];
  ctaClick: [result: any];
  addToCart: [productId: string];
}>();

const cardEl = ref<HTMLElement>();

// Use composables
const cardData = useCardData(props);
const pricing = usePricing(
  props,
  cardData.prices,
  cardData.isStoreAllProductsPage
);
const cardActions = useCardActions(props, emit);
const cardIcons = useCardIcons(
  cardData.cardData,
  cardData.essentialsContent,
  cardData.allAccessContent,
  cardData.freeContent,
  cardData.routerName.value
);

const cardHeading = computed((): string => {
  const baseHeading = cardData.displayName.value || "";

  if (props.result?.acronym) {
    return `${baseHeading} | ${props.result.acronym}`;
  }

  return baseHeading;
});

const showRelatedCertifications = computed((): boolean => {
  return (
    cardData.enableCertifications.value &&
    Boolean(cardData.resultCertifications.value) &&
    cardData.cardData.value.type !== "exam-voucher" &&
    cardData.cardData.value.type !== "lab"
  );
});

const showSubscriptionTag = computed(() => {
  return cardData.isStorePage.value && props.result?.lock_content === false;
});

const isCmlCloudPurchased = computed((): boolean => {
  const productGuid = pricing.priceDetails.value?.productGuid;
  if (!isCmlCloudProductId(productGuid)) return false;
  return props.result?.lock_content === false;
});

const enableRevUpRecert = ref(
  Statsig.checkGate(
    "enable_rev_up_recert",
    !!globalThis.env.ENABLE_REV_UP_RECERT
  )
);

const revUpCampaign = useRevUpCampaign(props, enableRevUpRecert);

onMounted(() => {
  if (cardEl.value) {
    cardActions.setupIntersectionObserver(cardEl.value);
  }
});
</script>

<template>
  <div data-cy="card-cu" ref="cardEl">
    <cu-new-card
      v-if="cardActions.isInViewPort.value && !props.isUnauth"
      :class="
        cardData.storePlansPage.value || cardData.contentBundlePage.value
          ? 'h-auto'
          : 'h-[22rem]'
      "
      :type="cardData.cardData.value.type"
      :bookmarked="cardData.inMyList.value(props.result)"
      :removable="cardData.canItBeRemoved.value"
      :admin="props.isAdminContent"
      :noAccess="cardData.isLocked.value"
      :vendor="cardData.cardData.value.vendor"
      :author="cardData.authorName.value"
      :list="props.cardView == 'Grid' ? false : true"
      :data-cy="props.cy"
      :link="props.link || undefined"
      :credits="pricing.displayedClcPrice.value"
      :price="pricing.displayedUsdPrice.value"
      :premium="
        ['exam-voucher', 'bundle', 'bundle-3', 'bundle-2', 'bundle-1'].includes(
          cardData.cardData.value.type
        ) || !!pricing.listprice.value
      "
      :listprice="pricing.listprice.value"
      :saletext="pricing.salePercentage.value"
      :nofooter="
        cardData.storePlansPage.value || cardData.contentBundlePage.value
      "
      :store="cardData.storePlansPage.value || cardData.contentBundlePage.value"
      @cu-card.prevent="cardActions.handleClick"
      @cu-card-bookmark.prevent="$emit('bookmark', props.result)"
      @cu-card-cta.prevent="$emit('ctaClick', props.result)"
    >
      <div slot="tags">
        <tag-list
          :content="props.result"
          :price-details="pricing.priceDetails.value"
          :isStorePage="cardData.isStorePage.value"
          :showSubscriptionTag="showSubscriptionTag"
          :showRevUpRecertCampaign="revUpCampaign.showRevUpRecertCampaign.value"
        />
      </div>

      <div v-if="showRelatedCertifications" slot="related-certs">
        {{ cardData.resultCertifications.value }}
      </div>
      <div slot="heading">
        <RawHtmlBlock :content="cardHeading" class="line-clamp-3 min-h-[4.5rem]" />
      </div>
      <div v-if="props.showDescription" slot="description">
        <p class="line-clamp-3 min-h-[3.6rem]">{{ props.result.summary }}</p>
      </div>
      <div slot="garden">
        <cu-icon-garden
          v-for="garden in cardIcons.filteredGarden.value"
          :key="garden.type"
          :type="cardIcons.iconType(garden.type)"
          :text="garden.text"
          :style="garden.style"
          horizontal="true"
        >
          <component
            :is="cardIcons.iconComponent(garden.type)"
            v-if="cardIcons.iconComponent(garden.type)"
            class="icon"
          ></component>

          <span
            v-if="cardIcons.isOneOfSpecificType(garden.type)"
            class="text"
            >{{ garden.text }}</span
          >
        </cu-icon-garden>
      </div>
      <div
        v-if="props.isAdminContent"
        slot="admin-tooltip"
        class="leading-normal tracking-wider"
      >
        As an admin, you can manage and remove your assigned learning through
        the
        <AnchorTargetBlank
          class="underline"
          :url="cardData.customerAdminLink.value"
          >{{ CUSTOMER_ADMIN_PORTAL_LABEL }}</AnchorTargetBlank
        >
      </div>
      <span slot="currency-type">
        <cu-tags
          :text="pricing.usdOrClcCurrency.value"
          bgColor="gray"
          size="s"
        />
      </span>
      <cu-button
        v-if="
          pricing.price.value &&
          !isCmlCloudPurchased &&
          (props.result?.type != 'challenge' ||
            (props.result?.type == 'challenge' && !showSubscriptionTag))
        "
        slot="actions"
        data-cy="card-add-to-cart"
        template="primary"
        @click.prevent="
          cardActions.addProduct(
            pricing.priceDetails.value.productGuid.toString(),
            props.result?.type
          )
        "
        size="s"
        >Add to cart</cu-button
      >
    </cu-new-card>
    <cu-new-card
      v-else-if="cardActions.isInViewPort.value && props.isUnauth"
      :class="
        cardData.storePlansPage.value || cardData.contentBundlePage.value
          ? 'h-auto'
          : 'h-[22rem]'
      "
      :type="cardData.cardData.value.type"
      nobookmark=""
      noAccess="true"
      :vendor="cardData.cardData.value.vendor"
      :author="cardData.authorName.value"
      :list="props.cardView == 'Grid' ? false : true"
      :data-cy="props.cy"
      :credits="pricing.displayedClcPrice.value"
      :price="pricing.displayedUsdPrice.value"
      :premium="
        ['exam-voucher', 'bundle', 'bundle-3', 'bundle-2', 'bundle-1'].includes(
          cardData.cardData.value.type
        ) || !!pricing.listprice.value
      "
      :listprice="pricing.listprice.value"
      :saletext="pricing.salePercentage.value"
      :nofooter="
        cardData.storePlansPage.value || cardData.contentBundlePage.value
      "
      :store="cardData.storePlansPage.value || cardData.contentBundlePage.value"
      :link="props.link || undefined"
      @click.prevent="false"
      @cu-card.prevent="$emit('navigate', props.result)"
      @cu-card-bookmark.prevent="$emit('bookmark', props.result)"
      @cu-card-cta.prevent="$emit('ctaClick', props.result)"
    >
      <div slot="tags">
        <tag-list
          :content="props.result"
          :price-details="pricing.priceDetails.value"
          :isStorePage="cardData.isStorePage.value"
          :showRevUpRecertCampaign="revUpCampaign.showRevUpRecertCampaign.value"
        />
      </div>
      <div v-if="showRelatedCertifications" slot="related-certs">
        {{ cardData.resultCertifications.value }}
      </div>
      <div slot="heading">
        <RawHtmlBlock :content="cardHeading" class="line-clamp-3 min-h-[4.5rem]" />
      </div>
      <div v-if="props.showDescription" slot="description">
        <p class="line-clamp-3 min-h-[3.6rem]">{{ props.result.summary }}</p>
      </div>
      <div slot="garden">
        <cu-icon-garden
          v-for="garden in cardIcons.filteredGarden.value"
          :key="garden.type"
          :type="cardIcons.iconType(garden.type)"
          :text="garden.text"
          :style="garden.style"
          horizontal="true"
        >
          <component
            :is="cardIcons.iconComponent(garden.type)"
            v-if="cardIcons.iconComponent(garden.type)"
            class="icon"
          ></component>

          <span
            v-if="cardIcons.isOneOfSpecificType(garden.type)"
            class="text"
            >{{ garden.text }}</span
          >
        </cu-icon-garden>
      </div>
      <span slot="currency-type">
        <cu-tags
          :text="pricing.usdOrClcCurrency.value"
          bgColor="gray"
          size="s"
        />
      </span>
      <cu-button
        slot="actions"
        template="primary"
        data-cy="card-signin-to-buy"
        @click.prevent="
          cardActions.navigateToLogin(cardData.routerName.value, props.link)
        "
        size="s"
        >Sign in to buy</cu-button
      >
    </cu-new-card>
    <div v-if="cardData.contentBundlePage.value">
      <button
        @click="cardActions.handleClick"
        @keyup.space="cardActions.handleClick"
        @keyup.enter="cardActions.handleClick"
        class="text-14 font-normal text-blue-light leading-[1.2] ml-4"
        v-if="!props.isUnauth"
      >
        View details
      </button>
      <button
        @click="$emit('navigate', props.result)"
        @keyup.space="$emit('navigate', props.result)"
        @keyup.enter="$emit('navigate', props.result)"
        class="text-14 font-normal text-blue-light leading-[1.2] ml-4"
        v-else
      >
        View details
      </button>
    </div>
  </div>
</template>

<style scoped>
[type="time"] {
  margin-right: 0.5rem;
  border: none;
  padding: 0;
  line-height: normal;
  background: none;
}
</style>
